'use client';

import { useMemo } from 'react';
import { useProducts } from './use-products';
import { useMoneyRules } from '@/lib/money-rules';
import type { ProductWithCategories } from '@/lib/api/products';

export type StatusRollTier = 'silver' | 'gold' | 'platinum';

export interface UseStatusRollsResult {
  /** Товары бонус-роллов по тиру, взятые из общего каталога. undefined — если каталог ещё грузится или ролл скрыт. */
  rolls: Record<StatusRollTier, ProductWithCategories | undefined>;
  /** Товар ролла по id (76/78/79). */
  getById: (id: number) => ProductWithCategories | undefined;
  /** Каталог ещё загружается. */
  isLoading: boolean;
}

/**
 * Статусные бонус-роллы (Silver/Gold/Platinum) из УЖЕ загруженного каталога
 * (`/products/with_categories`), без отдельных запросов `/products/:id`.
 *
 * Раньше 4 компонента (включая глобальную мини-корзину на каждой странице)
 * независимо догружали роллы 76/78/79 по одному. При интермиттентных сбоях
 * прокси это давало лавину "network error" в логе ошибок (каждый ретрай axios —
 * отдельная запись). Роллы и так присутствуют в каталоге, поэтому берём их оттуда:
 * когда каталог загружен — ноль дополнительных запросов.
 */
export function useStatusRolls(): UseStatusRollsResult {
  const { data: products, isLoading } = useProducts();
  // id подарочных роллов приходят с сервера и меняются в админке (смена
  // поставки). Раньше карта id → тир строилась один раз при загрузке модуля из
  // зашитых 76/78/79 — после замены подарка она молча указывала бы на старый
  // товар до перезагрузки страницы.
  const { status_rolls: statusRolls } = useMoneyRules();

  return useMemo<UseStatusRollsResult>(() => {
    const tierById = new Map<number, StatusRollTier>(
      statusRolls.map((roll) => [roll.product_id, roll.type])
    );

    const byId = new Map<number, ProductWithCategories>();
    for (const p of products ?? []) {
      if (tierById.has(p.id)) byId.set(p.id, p);
    }

    const idByTier = (tier: StatusRollTier): number | undefined =>
      statusRolls.find((roll) => roll.type === tier)?.product_id;

    const pick = (tier: StatusRollTier): ProductWithCategories | undefined => {
      const id = idByTier(tier);
      return id === undefined ? undefined : byId.get(id);
    };

    return {
      rolls: {
        silver: pick('silver'),
        gold: pick('gold'),
        platinum: pick('platinum'),
      },
      getById: (id: number) => byId.get(id),
      isLoading,
    };
  }, [products, isLoading, statusRolls]);
}
